import { Chip, Stack } from '@mui/material';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { SUPPORTED_LANGUAGE_CODES } from '@procureai/schemas';
import { FormFieldWrapper, TextAreaFieldWithController, TextFieldWithController } from '@procureai/ui-form-mui';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SvbrgDocumentVariant } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { DocumentUpload } from '~/components/FileUpload/DocumentUpload/DocumentUpload';
import { I18nOptionsAutocomplete } from '~/components/I18nOptionsAutocomplete/I18nOptionsAutocomplete';
import { useFormFieldErrorTranslation } from '~/hooks';

import { ProcurementKnowledgeDocumentData } from './ProcurementKnowledgeForm.schema';

// pick the variants and order we want to support
const DOCUMENT_VARIANTS = [
  SvbrgDocumentVariant.PROCUREMENT_POLICY,
  SvbrgDocumentVariant.SUPPLIER_TERMS,
  SvbrgDocumentVariant.OTHER,
];

export const ProcurementKnowledgeForm = () => {
  const { onError } = useCustomSnackbar();

  const { t } = useTranslation(I18nNamespace.knowledgeRepository, {
    keyPrefix: 'ProcurementKnowledge.ProcurementKnowledgeForm',
  });

  const { errorToHelperText } = useFormFieldErrorTranslation();
  const { control, setValue } = useFormContext<ProcurementKnowledgeDocumentData>();

  return (
    <Stack spacing={3}>
      {/* File field */}
      <Controller
        control={control}
        name="fileId"
        render={({ field, fieldState: { error } }) => (
          <FormFieldWrapper label={t('file')} error={!!error} required>
            {field.value ? (
              <div>
                <Chip
                  onDelete={() => setValue('fileId', '')}
                  label={<CuriosityValue ns={CuriosityNamespaceType.FILE} i18nKey={field.value} />}
                />
              </div>
            ) : (
              <DocumentUpload
                buttonLabel={t('uploadButton')}
                onUploadSuccess={(file) => {
                  setValue('fileId', file.uploadId);
                  // set title to file name without extension
                  setValue('title', file.fileName?.split('.').slice(0, -1).join('.') ?? '');
                }}
                onUploadFailed={onError}
                uploadIndicatorProps={{ messages: [t('uploadingLabel')] }}
              />
            )}
          </FormFieldWrapper>
        )}
      />

      {/* Title, Description, Content & URL */}
      <TextFieldWithController name={'title'} label={t('title')} control={control} required />
      <TextAreaFieldWithController
        helperText={t('description.helperText')}
        name={'description'}
        label={t('description.label')}
        control={control}
        rows={3}
        required
      />
      <TextAreaFieldWithController
        helperText={t('content.helperText')}
        name={'content'}
        label={t('content.label')}
        control={control}
        rows={3}
        required
      />

      <TextFieldWithController name={'url'} label={t('url')} control={control} />

      {/* Classification Select */}
      <Controller
        control={control}
        name="variant"
        render={({ field, fieldState: { error } }) => (
          <FormFieldWrapper
            label={t('variant')}
            error={!!error}
            helperText={error && errorToHelperText(error)}
            required
          >
            <I18nOptionsAutocomplete
              {...field}
              value={field.value || null}
              options={DOCUMENT_VARIANTS}
              i18nNs={I18nNamespace.knowledgeRepository}
              i18nKeyPrefix="ProcurementKnowledgeVariant"
              onChange={(_, items) => {
                field.onChange(items);
              }}
            />
          </FormFieldWrapper>
        )}
      />

      {/* Language Select */}
      <Controller
        control={control}
        name="language"
        render={({ field, fieldState: { error } }) => (
          <FormFieldWrapper
            label={t('language')}
            error={!!error}
            helperText={error && errorToHelperText(error)}
            required
          >
            <I18nOptionsAutocomplete
              {...field}
              value={field.value || null}
              options={SUPPORTED_LANGUAGE_CODES}
              i18nKeyPrefix="language"
              onChange={(_, items) => {
                field.onChange(items);
              }}
            />
          </FormFieldWrapper>
        )}
      />
    </Stack>
  );
};
